import { normalize, sub, v3, type Vec3 } from './math';
import type { GripPose, Handedness, PalmPose } from './handAnchor';

/**
 * WebXR readers for the hand-anchor inputs. Engine-facing (needs a live
 * `XRFrame`), kept apart from the pure `HandMenuAnchor` so the placement
 * logic stays testable.
 *
 * Joint convention (WebXR Hand Input): the wrist/metacarpal joint spaces have
 * +Y pointing OUT OF THE BACK of the hand and +Z from fingers toward the
 * wrist; the palm surface therefore faces -Y. IWSDK's `XROrigin` transforms
 * are applied by passing the origin's matrix so results land in world space.
 */

interface Quat {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly w: number;
}

function rotate(q: Quat, v: Vec3): Vec3 {
  // v' = q * v * q^-1, expanded.
  const { x, y, z, w } = q;
  const ix = w * v.x + y * v.z - z * v.y;
  const iy = w * v.y + z * v.x - x * v.z;
  const iz = w * v.z + x * v.y - y * v.x;
  const iw = -x * v.x - y * v.y - z * v.z;
  return v3(
    ix * w + iw * -x + iy * -z - iz * -y,
    iy * w + iw * -y + iz * -x - ix * -z,
    iz * w + iw * -z + ix * -y - iy * -x,
  );
}

/** Apply a world-from-reference transform (position offset + rotation) to a reference-space pose. */
export interface OriginTransform {
  readonly position: Vec3;
  readonly quaternion: Quat;
}

function toWorld(origin: OriginTransform | null, position: Vec3): Vec3 {
  if (!origin) return position;
  const rotated = rotate(origin.quaternion, position);
  return v3(rotated.x + origin.position.x, rotated.y + origin.position.y, rotated.z + origin.position.z);
}

function toWorldDir(origin: OriginTransform | null, direction: Vec3): Vec3 {
  return origin ? rotate(origin.quaternion, direction) : direction;
}

/**
 * The off hand's palm from hand tracking, or null when the hand is not
 * tracked this frame (controllers held, or tracking lost).
 */
export function readPalmPose(
  frame: XRFrame,
  session: XRSession,
  referenceSpace: XRReferenceSpace,
  handedness: Handedness,
  origin: OriginTransform | null,
): PalmPose | null {
  for (const source of session.inputSources) {
    if (source.handedness !== handedness || !source.hand) continue;
    const joint = source.hand.get('middle-finger-metacarpal') ?? source.hand.get('wrist');
    if (!joint || typeof frame.getJointPose !== 'function') return null;
    const pose = frame.getJointPose(joint, referenceSpace);
    if (!pose) return null;
    const p = pose.transform.position;
    const q = pose.transform.orientation;
    const local = v3(p.x, p.y, p.z);
    // Palm surface faces -Y of the joint frame.
    const palmNormal = normalize(toWorldDir(origin, rotate(q, v3(0, -1, 0))));
    return { position: toWorld(origin, local), palmNormal };
  }
  return null;
}

/** The off hand's controller grip pose, or null when no controller is held in that hand. */
export function readGripPose(
  frame: XRFrame,
  session: XRSession,
  referenceSpace: XRReferenceSpace,
  handedness: Handedness,
  origin: OriginTransform | null,
): GripPose | null {
  for (const source of session.inputSources) {
    if (source.handedness !== handedness || source.hand || !source.gripSpace) continue;
    const pose = frame.getPose(source.gripSpace, referenceSpace);
    if (!pose) return null;
    const p = pose.transform.position;
    const q = pose.transform.orientation;
    return {
      position: toWorld(origin, v3(p.x, p.y, p.z)),
      up: normalize(toWorldDir(origin, rotate(q, v3(0, 1, 0)))),
      right: normalize(toWorldDir(origin, rotate(q, v3(1, 0, 0)))),
      // WebXR grip space: -Z is forward (away from the wrist).
      forward: normalize(toWorldDir(origin, rotate(q, v3(0, 0, -1)))),
    };
  }
  return null;
}

/** Convenience: which hand is the OFF hand for a given pointer hand. */
export function offHandOf(pointerHand: Handedness): Handedness {
  return pointerHand === 'left' ? 'right' : 'left';
}

/** Re-exported for hosts that build a ViewerPose from three.js objects. */
export { sub as vecSub };
